import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader, TagPills } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { DownloadSimple, CheckSquare, FunnelSimple, X } from "@phosphor-icons/react";

export default function LedgerDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const monthFilter = searchParams.get("month"); // format YYYY-MM (optional, deep-link entry)
    const { user } = useAuth();
    const [ledger, setLedger] = useState(null);
    const [txns, setTxns] = useState([]);
    const [openOB, setOpenOB] = useState(false);
    const [openRec, setOpenRec] = useState(false);
    const [ob, setOb] = useState({ opening_balance: 0, opening_side: "dr", opening_date: "" });
    const [recDate, setRecDate] = useState("");
    // Free-form date range picker on top of (or overriding) the month filter.
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Effective range: explicit from/to wins over the month deep-link.
    const effectiveRange = useMemo(() => {
        if (dateFrom || dateTo) return { start: dateFrom || null, endExclusive: null, endInclusive: dateTo || null, label: `${dateFrom || "start"} → ${dateTo || "today"}` };
        if (monthFilter && /^\d{4}-\d{2}$/.test(monthFilter)) {
            const [y, m] = monthFilter.split("-").map(Number);
            const nextY = m === 12 ? y + 1 : y;
            const nextM = m === 12 ? 1 : m + 1;
            return {
                start: `${monthFilter}-01`,
                endExclusive: `${nextY}-${String(nextM).padStart(2, "0")}-01`,
                endInclusive: null,
                label: monthFilter,
            };
        }
        return null;
    }, [monthFilter, dateFrom, dateTo]);

    // Filter transactions to the selected range; keep a "carry-in" summary so
    // the running-balance column stays coherent when we hide earlier rows.
    const { filteredTxns, carryIn } = useMemo(() => {
        if (!effectiveRange) return { filteredTxns: txns, carryIn: null };
        const { start, endExclusive, endInclusive } = effectiveRange;
        const inRange = txns.filter((t) => {
            const d = (t.date || "").slice(0, 10);
            if (start && d < start) return false;
            if (endExclusive && d >= endExclusive) return false;
            if (endInclusive && d > endInclusive) return false;
            return true;
        });
        const before = start ? txns.filter((t) => (t.date || "").slice(0, 10) < start) : [];
        const openingBal = before.length > 0 ? before[before.length - 1].balance : 0;
        const totalIn = inRange.reduce((s, t) => s + (t.delta > 0 ? t.delta : 0), 0);
        const totalOut = inRange.reduce((s, t) => s + (t.delta < 0 ? -t.delta : 0), 0);
        return {
            filteredTxns: inRange,
            carryIn: {
                openingBal,
                closingBal: inRange.length > 0 ? inRange[inRange.length - 1].balance : openingBal,
                totalIn,
                totalOut,
                count: inRange.length,
            },
        };
    }, [txns, effectiveRange]);

    const clearMonth = () => {
        const s = new URLSearchParams(searchParams);
        s.delete("month");
        setSearchParams(s, { replace: true });
        setDateFrom(""); setDateTo("");
    };

    const load = async () => {
        try {
            const [l, t] = await Promise.all([api.get(`/ledgers/${id}`), api.get(`/ledgers/${id}/transactions`)]);
            setLedger(l.data);
            setTxns(t.data.transactions);
            setOb({ opening_balance: l.data.opening_balance || 0, opening_side: l.data.opening_side || "dr", opening_date: l.data.opening_date || "" });
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [id]);

    const setOpening = async () => {
        try {
            await api.patch(`/ledgers/${id}/opening`, { opening_balance: Number(ob.opening_balance), opening_side: ob.opening_side, opening_date: ob.opening_date || null });
            toast.success("Opening balance saved"); setOpenOB(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reconcile = async () => {
        try { await api.post(`/ledgers/${id}/reconcile`, { reconciled_upto: recDate }); toast.success("Reconciliation marked"); setOpenRec(false); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const downloadXlsx = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ledgers/${id}/download.xlsx`, "_blank");
    const downloadPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ledgers/${id}/download.pdf`, "_blank");

    if (!ledger) return <div className="p-8 text-stone-500">Loading…</div>;
    const canEdit = user?.role === "founder" || user?.role === "accountant";
    const recUpto = ledger.reconciled_upto;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title={ledger.name}
                sub={<>{ledger.group?.name} · {ledger.party_type}{ledger.gstin ? ` · ${ledger.gstin}` : ""}</>}
                right={
                    <>
                        <div className="text-right mr-4">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Closing balance</div>
                            <div className="font-mono text-2xl tabular-nums" data-testid="closing-balance">₹ {inr(ledger.closing_balance)}</div>
                        </div>
                        {canEdit ? (
                            <>
                                <Dialog open={openOB} onOpenChange={setOpenOB}>
                                    <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="set-opening-btn">Opening balance</Button></DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader><DialogTitle className="font-serif text-2xl">Opening balance</DialogTitle></DialogHeader>
                                        <div className="space-y-4">
                                            <div><Label>Amount</Label><Input type="number" value={ob.opening_balance} onChange={(e) => setOb({ ...ob, opening_balance: e.target.value })} data-testid="ob-amt" /></div>
                                            <div><Label>Side</Label>
                                                <Select value={ob.opening_side} onValueChange={(v) => setOb({ ...ob, opening_side: v })}>
                                                    <SelectTrigger data-testid="ob-side"><SelectValue /></SelectTrigger>
                                                    <SelectContent><SelectItem value="dr">Balance owed to us / cash we have (Dr)</SelectItem><SelectItem value="cr">We owe / income (Cr)</SelectItem></SelectContent>
                                                </Select>
                                            </div>
                                            <div><Label>As of</Label><Input type="date" value={ob.opening_date} onChange={(e) => setOb({ ...ob, opening_date: e.target.value })} data-testid="ob-date" /></div>
                                        </div>
                                        <DialogFooter><Button onClick={setOpening} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="ob-save">Save</Button></DialogFooter>
                                    </DialogContent>
                                </Dialog>
                                <Dialog open={openRec} onOpenChange={setOpenRec}>
                                    <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="reconcile-btn"><CheckSquare size={14} className="mr-1" /> Reconciled up to</Button></DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader><DialogTitle className="font-serif text-2xl">Mark reconciled up to</DialogTitle></DialogHeader>
                                        <div className="space-y-4"><div><Label>Date</Label><Input type="date" value={recDate} onChange={(e) => setRecDate(e.target.value)} data-testid="rec-date" /></div></div>
                                        <DialogFooter><Button onClick={reconcile} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="rec-save">Mark</Button></DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={downloadXlsx} data-testid="dl-xlsx"><DownloadSimple size={14} className="mr-1" /> XLSX</Button>
                        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="dl-pdf"><DownloadSimple size={14} className="mr-1" /> PDF</Button>
                    </>
                } />
            <div className="p-8">
                {ledger.party_type === "customer" ? <CustomerNameParts ledger={ledger} canEdit={canEdit} onSaved={load} /> : null}
                {/* Date-range picker — always visible, sits above any deep-linked month filter strip */}
                <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="ldr-date-range">
                    <span className="text-xs text-stone-500">Show period:</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                           className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                           title="From date" data-testid="ldr-date-from" />
                    <span className="text-xs text-stone-400">to</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                           className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                           title="To date" data-testid="ldr-date-to" />
                    {(dateFrom || dateTo) ? (
                        <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }}
                                className="text-[11px] text-stone-500 hover:text-stone-800 px-1"
                                data-testid="ldr-date-clear">Clear</button>
                    ) : null}
                    {/* Quick shortcuts */}
                    {[
                        ["This month", () => {
                            const d = new Date();
                            const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0");
                            setDateFrom(`${y}-${m}-01`); setDateTo("");
                        }],
                        ["Last month", () => {
                            const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-1);
                            const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0");
                            const last = new Date(y, d.getMonth()+1, 0).getDate();
                            setDateFrom(`${y}-${m}-01`); setDateTo(`${y}-${m}-${String(last).padStart(2,"0")}`);
                        }],
                        ["This FY", () => {
                            const d = new Date();
                            const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
                            setDateFrom(`${fyStartYear}-04-01`); setDateTo("");
                        }],
                    ].map(([label, fn]) => (
                        <button key={label} type="button" onClick={fn}
                                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border text-stone-600 hover:bg-stone-50"
                                style={{ borderColor: "var(--se-border)" }}
                                data-testid={`ldr-quick-${label.toLowerCase().replace(/\s/g,'-')}`}>
                            {label}
                        </button>
                    ))}
                </div>
                {effectiveRange ? (
                    <div className="mb-4 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm"
                         data-testid="month-filter-strip">
                        <FunnelSimple size={16} weight="duotone" className="text-blue-700" />
                        <div className="flex-1">
                            <div className="text-blue-900">
                                Showing <b>{effectiveRange.label}</b> · {carryIn?.count ?? 0} transactions
                            </div>
                            {carryIn ? (
                                <div className="text-[11px] text-blue-800 mt-0.5">
                                    Carry-in: <b>₹ {inr(carryIn.openingBal)}</b> · In: <span className="text-emerald-700">+ ₹ {inr(carryIn.totalIn)}</span> · Out: <span className="text-rose-700">− ₹ {inr(carryIn.totalOut)}</span> · Closing: <b>₹ {inr(carryIn.closingBal)}</b>
                                </div>
                            ) : null}
                        </div>
                        {monthFilter ? (
                            <Link to={`/reports/monthly-pnl?month=${monthFilter}`} className="text-xs text-blue-700 underline" data-testid="month-filter-back-pnl">
                                Back to P&L
                            </Link>
                        ) : null}
                        <Button size="sm" variant="outline" onClick={clearMonth} data-testid="month-filter-clear">
                            <X size={12} className="mr-1" /> Clear
                        </Button>
                    </div>
                ) : null}
                {recUpto ? (
                    <div className="mb-4 text-xs px-3 py-2 inline-block rounded-sm" style={{ background: "var(--se-brand-soft)", color: "var(--se-brand)" }} data-testid="rec-marker">
                        Reconciled up to <b>{formatDate(recUpto)}</b>. Anything after is unreconciled.
                    </div>
                ) : null}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="txn-table">
                        <thead><tr>
                            <th>Date</th><th>Narration</th><th>Tags</th>
                            <th className="text-right">In / Out</th><th className="text-right">Running balance</th>
                        </tr></thead>
                        <tbody>
                            {filteredTxns.length === 0 ? <tr><td colSpan="5" className="text-stone-500 py-8 text-center">{effectiveRange ? `No transactions in ${effectiveRange.label}.` : "No transactions yet."}</td></tr> :
                                filteredTxns.map((t, i) => {
                                    // Some legacy rows store date as "YYYY-MM-DD HH:MM:SS".
                                    // Strip the time part so string-compare with recUpto ("YYYY-MM-DD") is honest.
                                    const txnDay = (t.date || "").slice(0, 10);
                                    const afterRec = recUpto && txnDay > recUpto;
                                    return (
                                        <tr key={i} data-testid={`txn-${i}`} style={afterRec ? { background: "#fbfaf7" } : {}}>
                                            <td className="font-mono text-xs">{formatDate(t.date)}{afterRec ? <div className="se-chip se-chip-warn mt-1">unreconciled</div> : null}</td>
                                            <td>
                                                <div className="text-sm">{t.narration}</div>
                                                <div className="text-[10px] text-stone-500 uppercase tracking-widest">{t.source_type}</div>
                                            </td>
                                            <td><TagPills dept={t.dept} location={t.location} /></td>
                                            <td className="se-num">
                                                {t.delta >= 0 ? <span style={{ color: "#1e4620" }}>+ ₹{inr(t.delta)}</span> : <span style={{ color: "#991b1b" }}>- ₹{inr(-t.delta)}</span>}
                                            </td>
                                            <td className="se-num">₹ {inr(t.balance)}</td>
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

function CustomerNameParts({ ledger, canEdit, onSaved }) {
    const p = ledger.name_parts || {};
    const [form, setForm] = useState({
        advance_name: p.advance_name || "",
        advance_phone: p.advance_phone || "",
        order_name: p.order_name || "",
        invoice_name: p.invoice_name || "",
    });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/ledgers/${ledger.id}/name-parts`, {
                advance_name: form.advance_name || null,
                advance_phone: form.advance_phone || null,
                order_name: form.order_name || null,
                invoice_name: form.invoice_name || null,
            });
            toast.success("Ledger name updated");
            setDirty(false);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="mb-6 bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="name-parts-card">
            <div className="px-5 py-3 border-b text-xs uppercase tracking-widest" style={{ borderColor: "var(--se-border)", color: "var(--se-plum)" }}>
                Identity across advance · order · invoice
            </div>
            <div className="p-5 grid grid-cols-4 gap-4 items-end">
                <div><Label>Advance name (A)</Label><Input value={form.advance_name} onChange={(e) => set("advance_name", e.target.value)} placeholder="e.g. Rahul" disabled={!canEdit} data-testid="np-advance" /></div>
                <div><Label>Advance phone</Label><Input value={form.advance_phone} onChange={(e) => set("advance_phone", e.target.value)} placeholder="10-digit" disabled={!canEdit} data-testid="np-phone" /></div>
                <div><Label>Order-punched name (O)</Label><Input value={form.order_name} onChange={(e) => set("order_name", e.target.value)} placeholder="e.g. Geeta" disabled={!canEdit} data-testid="np-order" /></div>
                <div><Label>Invoice / Billing name (I)</Label><Input value={form.invoice_name} onChange={(e) => set("invoice_name", e.target.value)} placeholder="e.g. Green Furniture Pvt Ltd" disabled={!canEdit} data-testid="np-invoice" /></div>
                <div className="col-span-4 flex justify-end">
                    <Button size="sm" onClick={save} disabled={!dirty || saving || !canEdit} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="np-save">
                        {saving ? "Saving…" : "Save name parts"}
                    </Button>
                </div>
                <p className="col-span-4 text-[11px] text-stone-500 -mt-1">Display name is auto-composed as <b>Advance(A) · Order(O) · Invoice(I)</b>. Journals reference the ledger by ID so renames are safe.</p>
            </div>
        </div>
    );
}

